import { CAMINHO_OAUTH_GOOGLE } from "@base/contracts";
import {
  Button,
  CampoDeTexto,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  cn,
  toast,
} from "@base/ui";
import { ArrowRight, KeyRound, LoaderCircle, LogIn, ShieldCheck, Unplug } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { Link } from "../../../shared/navegacao/navegacao";
import { formatar } from "../../dashboard";
import {
  useChaveDeIa,
  useConcluirOauthGoogle,
  useIniciarOauthGoogle,
  useRemoverChaveDeIa,
  useRemoverCredencialGoogle,
  useSalvarChaveDeIa,
  useSalvarCredencialMeta,
} from "../application/conexoes.contexto";

/**
 * Acessos da Monte ao Meta (token do usuario do sistema da BM) e ao Google Ads
 * (login com a conta que ve a MCC). Um so de cada, para todas as clinicas;
 * so a equipe Monte troca. Ficam em Configuracoes da Monte.
 */

const MENSAGEM_OAUTH = "monte-saude:google-ads-oauth";

export const CAMINHO_DO_MONITORAMENTO = "/monte/monitoramento-de-contas";
export const CAMINHO_DAS_CONEXOES_DA_MONTE = "/monte/conexoes";

/**
 * O acesso da Monte visto de dentro de uma clinica: so o estado. Quem troca e
 * a equipe Monte, em Configuracoes da Monte.
 */
export function ResumoDoAcessoDaMonte({
  plataforma,
  configurada,
  nome,
  equipeMonte,
}: {
  readonly plataforma: "Meta" | "Google Ads";
  readonly configurada: boolean;
  readonly nome: string | null;
  readonly equipeMonte: boolean;
}) {
  return (
    <section className="flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70">
      <div className="flex items-center gap-4">
        <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-secondary text-ice">
          <ShieldCheck className="size-5" aria-hidden="true" />
        </span>
        <div className="flex flex-col">
          <h2 className="text-lg font-medium">Acesso da Monte ao {plataforma}</h2>
          <p className="text-sm text-muted-foreground">
            {configurada
              ? `Conectado como ${nome ?? plataforma}. Vale para todas as clínicas.`
              : "Ainda não configurado. A equipe Monte configura uma vez, para todas as clínicas."}
          </p>
        </div>
      </div>
      {equipeMonte ? (
        <Button variant="outline" asChild>
          <Link para={CAMINHO_DAS_CONEXOES_DA_MONTE}>
            {configurada ? "Gerenciar acesso" : "Configurar acesso"}
            <ArrowRight aria-hidden="true" />
          </Link>
        </Button>
      ) : null}
    </section>
  );
}

export function AcessoDaMonteAoMeta({
  nomeNoMeta,
  atualizadaEm,
  configurada,
  podeEditar,
  aoConectar,
}: {
  readonly nomeNoMeta: string | null;
  readonly atualizadaEm: string | null;
  readonly configurada: boolean;
  readonly podeEditar: boolean;
  /** Depois de salvar o token (ex.: ler os saldos de novo). */
  readonly aoConectar?: () => void;
}) {
  const [trocando, setTrocando] = useState(!configurada);
  const [token, setToken] = useState("");
  const salvar = useSalvarCredencialMeta();

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate(token, {
      onSuccess: () => {
        setToken("");
        setTrocando(false);
        toast.success("Acesso ao Meta salvo");
        aoConectar?.();
      },
    });
  }

  return (
    <section className="flex h-full flex-col gap-5 rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-secondary text-ice">
            <ShieldCheck className="size-5" aria-hidden="true" />
          </span>
          <div className="flex flex-col">
            <h2 className="text-lg font-medium">Token da BM do Meta</h2>
            <p className="text-sm text-muted-foreground">
              {configurada
                ? `Conectado como ${nomeNoMeta}${atualizadaEm ? ` · desde ${formatar.momento(atualizadaEm)}` : ""}. Vale para todas as clínicas.`
                : podeEditar
                  ? "Cole o token do usuário do sistema do Business Manager da Monte (permissão ads_read)."
                  : "Ainda não configurado. A equipe Monte configura uma vez, para todas as clínicas."}
            </p>
          </div>
        </div>
        {configurada && podeEditar && !trocando ? (
          <Button variant="outline" onClick={() => setTrocando(true)}>
            <KeyRound aria-hidden="true" />
            Trocar token
          </Button>
        ) : null}
      </div>

      {podeEditar && trocando ? (
        <form onSubmit={enviar} noValidate className="flex max-w-xl flex-col gap-4">
          <CampoDeTexto
            rotulo="Token do Meta"
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={token}
            onChange={(evento) => setToken(evento.target.value)}
            dica="É testado no Meta antes de salvar, fica cifrado e nunca volta a aparecer na tela."
            required
          />
          {salvar.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {mensagemDoErro(salvar.error)}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button type="submit" disabled={salvar.isPending || token.trim().length < 20}>
              {salvar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Salvar token
            </Button>
            {configurada ? (
              <Button type="button" variant="ghost" onClick={() => setTrocando(false)}>
                Cancelar
              </Button>
            ) : null}
          </div>
        </form>
      ) : null}
    </section>
  );
}

export function AcessoDaMonteAoGoogle({
  nomeNoGoogle,
  atualizadaEm,
  configurada,
  podeEditar,
  aoConectar,
}: {
  readonly nomeNoGoogle: string | null;
  readonly atualizadaEm: string | null;
  readonly configurada: boolean;
  readonly podeEditar: boolean;
  /** Depois de entrar com o Google (ex.: ler os saldos de novo). */
  readonly aoConectar?: () => void;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const iniciar = useIniciarOauthGoogle();
  const concluir = useConcluirOauthGoogle();
  const remover = useRemoverCredencialGoogle();

  useEffect(() => {
    if (!podeEditar) return;
    function ouvir(evento: MessageEvent) {
      if (evento.origin !== window.location.origin) return;
      const dados = evento.data as { tipo?: string; code?: string; state?: string; erro?: string } | null;
      if (!dados || dados.tipo !== MENSAGEM_OAUTH) return;
      if (dados.erro) {
        toast.error("O Google não autorizou o acesso.");
        return;
      }
      if (dados.code && dados.state) {
        concluir.mutate(
          { code: dados.code, state: dados.state },
          {
            onSuccess: () => {
              toast.success("Google Ads conectado.");
              aoConectar?.();
            },
          },
        );
      }
    }
    window.addEventListener("message", ouvir);
    return () => window.removeEventListener("message", ouvir);
  }, [podeEditar, concluir, aoConectar]);

  function entrarComGoogle() {
    const popup = window.open("about:blank", "google-ads-oauth", "popup=yes,width=480,height=720");
    iniciar.mutate(undefined, {
      onSuccess: ({ url }) => {
        if (popup && !popup.closed) popup.location.href = url;
        else window.location.href = url;
      },
      onError: () => popup?.close(),
    });
  }

  const ocupado = iniciar.isPending || concluir.isPending || remover.isPending;
  const erro = iniciar.error ?? concluir.error ?? remover.error;

  return (
    <section
      className={cn(
        "flex h-full flex-col gap-6 rounded-3xl p-6 md:p-8",
        configurada
          ? "bg-card shadow-sm ring-1 ring-border/70"
          : "bg-secondary text-secondary-foreground",
      )}
    >
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <span
            className={cn(
              "grid size-14 shrink-0 place-items-center rounded-2xl text-lg font-semibold tracking-tight",
              configurada ? "bg-accent text-accent-foreground" : "bg-white/10 text-ice",
            )}
            aria-hidden="true"
          >
            G
          </span>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-ice">
              {configurada ? "Conectado" : "Aguardando"}
            </p>
            <h2 className="text-2xl font-medium tracking-tight">
              {configurada ? (nomeNoGoogle ?? "Google Ads") : "Entre com o Google Ads"}
            </h2>
            <p className={cn("max-w-xl text-sm leading-relaxed", configurada ? "text-muted-foreground" : "text-secondary-foreground/75")}>
              {configurada
                ? `Acesso da Monte${atualizadaEm ? ` · desde ${formatar.momento(atualizadaEm)}` : ""}. Vale para todas as clínicas.`
                : podeEditar
                  ? "Um clique abre o Google. A conta que você autorizar enxerga as contas de anúncio da Monte."
                  : "A equipe Monte conecta uma vez. Depois, cada clínica escolhe as próprias contas."}
            </p>
          </div>
        </div>

        {podeEditar ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={configurada ? "outline" : "default"}
              onClick={entrarComGoogle}
              disabled={ocupado}
              className={!configurada ? "bg-card text-card-foreground hover:bg-card/90" : undefined}
            >
              {iniciar.isPending || concluir.isPending ? (
                <LoaderCircle className="animate-spin" aria-hidden="true" />
              ) : (
                <LogIn aria-hidden="true" />
              )}
              {configurada ? "Trocar conta" : "Entrar com o Google"}
            </Button>
            {configurada ? (
              <Button type="button" variant="destructive" onClick={() => setConfirmando(true)} disabled={ocupado}>
                <Unplug aria-hidden="true" />
                Desconectar
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {erro ? (
        <p role="alert" className="rounded-2xl bg-destructive/15 px-4 py-3 text-sm text-destructive">
          {mensagemDoErro(erro)}
        </p>
      ) : null}

      <Dialog open={confirmando} onOpenChange={setConfirmando}>
        <DialogContent>
          <DialogHeader rotulo="Google Ads">
            <DialogTitle>Desconectar o acesso da Monte?</DialogTitle>
            <DialogDescription>
              As clínicas deixam de listar contas até alguém da Monte entrar de novo com o Google.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => setConfirmando(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={remover.isPending}
              onClick={() =>
                remover.mutate(undefined, {
                  onSuccess: () => {
                    setConfirmando(false);
                    toast.success("Google Ads desconectado.");
                  },
                })
              }
            >
              {remover.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Desconectar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/**
 * Retorno do login do Google. O Google devolve o popup para CAMINHO_OAUTH_GOOGLE
 * (o endereco registrado no Google Cloud); dali o popup repassa o codigo para a
 * janela que abriu o login e fecha. Sem popup (bloqueado), conclui aqui mesmo.
 */
export function useRetornoDoOauthGoogle(podeEditar: boolean) {
  const concluir = useConcluirOauthGoogle();
  useEffect(() => {
    if (!podeEditar) return;
    const busca = new URLSearchParams(window.location.search);
    const code = busca.get("code");
    const state = busca.get("state");
    const erro = busca.get("error");
    if (!code && !state && !erro) return;
    if (window.opener) {
      window.opener.postMessage({ tipo: MENSAGEM_OAUTH, code, state, erro }, window.location.origin);
      window.close();
      return;
    }
    window.history.replaceState(null, "", CAMINHO_OAUTH_GOOGLE);
    if (erro) {
      toast.error("O Google não autorizou o acesso.");
      return;
    }
    if (code && state) {
      concluir.mutate({ code, state }, { onSuccess: () => toast.success("Google Ads conectado.") });
    }
    // So na chegada a pagina: o codigo do Google vale uma vez.
  }, [podeEditar]);
}

export function AcessoDaMonteAOpenAi({ podeEditar }: { readonly podeEditar: boolean }) {
  const { data, isPending, isError, error, refetch } = useChaveDeIa();
  const salvar = useSalvarChaveDeIa();
  const remover = useRemoverChaveDeIa();
  const [chave, setChave] = useState("");
  const [trocando, setTrocando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const configurada = data?.configurada ?? false;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate(chave, {
      onSuccess: () => {
        setChave("");
        setTrocando(false);
        toast.success("Chave da OpenAI salva");
      },
    });
  }

  if (isPending) {
    return (
      <section className="rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70">
        <h2 className="text-lg font-medium">Chave da OpenAI</h2>
      </section>
    );
  }
  if (isError) {
    return (
      <section className="rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70">
        <p role="alert" className="text-sm text-destructive">
          {mensagemDoErro(error)}
        </p>
        <Button variant="outline" onClick={() => refetch()}>
          Tentar de novo
        </Button>
      </section>
    );
  }

  return (
    <section className="flex h-full flex-col gap-5 rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-secondary text-ice">
            <KeyRound className="size-5" aria-hidden="true" />
          </span>
          <div className="flex flex-col">
            <h2 className="text-lg font-medium">Chave da OpenAI</h2>
            <p className="text-sm text-muted-foreground">
              {configurada
                ? `Conectada${data?.atualizadaEm ? ` · desde ${formatar.momento(data.atualizadaEm)}` : ""}. Vale para todas as clínicas.`
                : "Cole a chave da OpenAI da Monte. É confirmada antes de salvar e nunca volta a aparecer."}
            </p>
          </div>
        </div>
        {configurada && podeEditar && !trocando ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTrocando(true)}>
              Trocar chave
            </Button>
            {confirmando ? (
              <Button
                variant="outline"
                disabled={remover.isPending}
                onClick={() =>
                  remover.mutate(undefined, {
                    onSuccess: () => {
                      setConfirmando(false);
                      toast.success("Chave da OpenAI removida");
                    },
                  })
                }
              >
                Confirmar remoção
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmando(true)}>
                Remover chave
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {podeEditar && (!configurada || trocando) ? (
        <form onSubmit={enviar} noValidate className="flex max-w-xl flex-col gap-4">
          <CampoDeTexto
            rotulo="Chave da OpenAI"
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={chave}
            onChange={(evento) => setChave(evento.target.value)}
            dica="É confirmada na OpenAI antes de salvar, fica cifrada e nunca volta a aparecer."
            required
          />
          {salvar.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {mensagemDoErro(salvar.error)}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button type="submit" disabled={salvar.isPending || chave.trim().length < 20}>
              {salvar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Salvar chave
            </Button>
            {configurada ? (
              <Button type="button" variant="ghost" onClick={() => setTrocando(false)}>
                Cancelar
              </Button>
            ) : null}
          </div>
        </form>
      ) : null}
    </section>
  );
}
